import React, { useEffect, useRef, useState } from "react";
import ReactTestUtils from "react-dom/test-utils";
import Keyboard, { KeyboardReactInterface } from "react-simple-keyboard";
import "react-simple-keyboard/build/css/index.css";
import { SimpleKeyboardLayouts } from "simple-keyboard-layouts";
import {
  LayoutItem,
  LayoutItemObj,
} from "simple-keyboard-layouts/build/interfaces";
import { useLocale } from "@davi-ai/retorik-framework";
import langToLanguage from "../translations/langToLanguage.json";
import translation from "../translations/keyboard.json";

import CloseIcon from "./icons/CloseIcon";

const layouts = new SimpleKeyboardLayouts();

const VirtualKeyboard = () => {
  const locale: string = useLocale();
  const [observer] = useState<MutationObserver>(
    new MutationObserver((mutations) => handleMutation(mutations))
  );
  const [shadowObserver] = useState<MutationObserver>(
    new MutationObserver((mutations) => handleShadowMutation(mutations))
  );
  const [currentLayout, setCurrentLayout] = useState<
    LayoutItem | LayoutItemObj
  >();
  const [currentLayoutName, setCurrentLayoutName] = useState<string>("default");
  const [displayKeyboard, setDisplayKeyboard] = useState<boolean>(false);
  const [isDragging, setIsDragging] = useState<boolean>(false);
  const [position, setPosition] = useState<{ x: number; y: number }>({
    x: window.innerWidth > window.innerHeight ? window.innerWidth / 2 : window.innerWidth / 8,
    y: window.innerHeight / 2,
  });

  const divRef = useRef<HTMLDivElement | null>(null);
  const positionRef = useRef<{ x: number; y: number }>({
    x: window.innerWidth / 2,
    y: window.innerHeight / 2,
  });
  const isDraggingRef = useRef<boolean>(false);
  const keyboardRef = useRef<KeyboardReactInterface | null>(null);
  const keyboardTargetRef = useRef<
    HTMLTextAreaElement | HTMLInputElement | null
  >(null);
  const retorikShadowRef = useRef<ShadowRoot | null>(null);
  const blurTimerRef = useRef<NodeJS.Timer | null>(null);

  useEffect(() => {
    const lang = locale.split("-")[0];
    const language: string = (
      langToLanguage[lang] || langToLanguage["en"]
    ).name.toLowerCase();
    setCurrentLayout(layouts.get(language));
  }, [locale]);

  useEffect(() => {
    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: false,
      characterData: false,
    });
  }, [observer]);

  const handleMutation = (mutations: Array<MutationRecord>): void => {
    mutations.forEach((mutation) => {
      if (mutation.addedNodes.length) {
        const shadow = Array.from(mutation.addedNodes).find(
          // @ts-ignore
          (node) => node.id === "retorik-shadow"
        );
        const shadowRoot = shadow
          ? (shadow as HTMLElement).shadowRoot
          : undefined;
        if (shadowRoot) {
          retorikShadowRef.current = shadowRoot;
          shadowObserver.observe(shadowRoot, {
            childList: true,
            subtree: true,
            attributes: false,
            characterData: false,
          });
        } else {
          processNode(mutation.addedNodes[0] as Element);
        }
      }
    });
  };

  const handleShadowMutation = (mutations: Array<MutationRecord>): void => {
    mutations.forEach((mutation) => {
      if (mutation.addedNodes.length) {
        Array.from(mutation.addedNodes).forEach((node) => {
          processNode(node as Element);
        });
      }
    });
  };

  const processNode = (node: Element): void => {
    switch (node.tagName?.toLowerCase()) {
      case "textarea":
        addKeyboardTarget(node as HTMLTextAreaElement);
        break;
      case "input":
        addKeyboardTarget(node as HTMLInputElement);
        break;
    }
    node.children?.length && processChildren(node.children);
  };

  const processChildren = (children: HTMLCollection): void => {
    Array.from(children).forEach((child) => {
      processNode(child);
    });
  };

  const addKeyboardTarget = (
    target: HTMLTextAreaElement | HTMLInputElement
  ): void => {
    target.addEventListener("focus", (e) => handleFocus(e));
    target.addEventListener("blur", handleBlur);
    // Check if the element is already focused in the document or in retorik-shadow to show the keyboard
    if (
      document.activeElement === target ||
      retorikShadowRef.current?.activeElement === target
    ) {
      handleFocus({ target: target });
    }
  };

  const handleFocus = (e): void => {
    // Kill blur timetout to prevent keyboard closing whn switching from one input to another
    blurTimerRef?.current && clearTimeout(blurTimerRef.current);
    setDisplayKeyboard(true);
    keyboardTargetRef.current = e.target;
    // Get current input value
    keyboardRef.current?.setInput(keyboardTargetRef.current?.value || "");
  };

  const handleBlur = (): void => {
    blurTimerRef?.current && clearTimeout(blurTimerRef.current);
    blurTimerRef.current = setTimeout(() => {
      setDisplayKeyboard(false);
    }, 100);
  };

  // Keep keyboard visible if the loss of focus on the target is a click on the keyboard
  const handleClick = (): void => {
    blurTimerRef?.current && clearTimeout(blurTimerRef.current);
    setDisplayKeyboard(true);
  };

  const handleKeyReleased = (button: string): void => {
    // Handle shift button
    if (button === "{shift}" || button === "{lock}") {
      toggleShift();
    } else if (button === "{enter}" && keyboardTargetRef.current) {
      ReactTestUtils.Simulate.keyDown(keyboardTargetRef.current, {
        key: "Enter",
      });
    }
  };

  // Fill target's value with keyboard's input
  const handleChange = (input: string): void => {
    if (
      keyboardTargetRef?.current &&
      input !== keyboardTargetRef.current.value
    ) {
      keyboardTargetRef.current.value = input;
      ReactTestUtils.Simulate.change(keyboardTargetRef.current);
    }
  };

  const toggleShift = (): void => {
    setCurrentLayoutName((current) =>
      current === "default" ? "shift" : "default"
    );
  };

  const handlePointerDown = (event) => {
    setIsDragging(true);
    isDraggingRef.current = true;
    const rect = divRef.current?.getBoundingClientRect();
    if (rect) {
      const x = event.clientX - rect.left;
      const y = event.clientY - rect.top;
      positionRef.current = { x: x, y: y };
    }
    event.preventDefault();
    event.stopPropagation();
  };

  const handlePointerMove = (event) => {
    if (isDraggingRef.current) {
      const x = event.clientX - positionRef.current.x;
      const y = event.clientY - positionRef.current.y;
      setPosition({ x: x, y: y });
      event.preventDefault();
      event.stopPropagation();
    }
  };

  const handlePointerUp = () => {
    setIsDragging(false);
    isDraggingRef.current = false;
  };

  const handleClose = (event): void => {
    // Remove focus from the current input, that will hide the keyboard
    keyboardTargetRef?.current?.blur();
    event.preventDefault();
    event.stopPropagation();
  };

  return (
    <div
      ref={divRef}
      className="absolute z-[100] w-full px-4 max-w-[800px]"
      style={{
        display: displayKeyboard ? "block" : "none",
        top: position.y,
        left: position.x,
      }}
      onClick={handleClick}
    >
      <div
        className="h-12 w-full -mb-1 flex flex-row items-center rounded-t-lg bg-[#ECECEC] touch-none"
        style={{
          cursor: isDragging ? "grabbing" : "grab",
        }}
        onPointerDown={handlePointerDown}
        onPointerUp={handlePointerUp}
        onPointerMove={handlePointerMove}
      >
        <div className="w-full p-1 text-center">
          {translation[locale]?.drag || translation["fr-FR"].drag}
        </div>
        <div
          onClick={(e) => handleClose(e)}
          className="h-full w-fit p-1 flex items-center justify-center hover:cursor-pointer"
        >
          <CloseIcon />
        </div>
      </div>
      <Keyboard
        keyboardRef={(ref) => (keyboardRef.current = ref)}
        {...currentLayout}
        layoutName={currentLayoutName}
        onKeyReleased={(button) => handleKeyReleased(button)}
        onChange={(input) => handleChange(input)}
        preventMouseDownDefault={true}
        preventMouseUpDefault={true}
        stopMouseDownPropagation={true}
        stopMouseUpPropagation={true}
      />
    </div>
  );
};

export default VirtualKeyboard;
